import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/delivery.scss";

const formatCurrency = (amount) =>
  new Intl.NumberFormat(siteConfig.currency.locale, {
    style: "currency",
    currency: siteConfig.currency.code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

function Delivery() {
  return (
    <main className="shipping-page policy-page" id="main-content">
      <header className="policy-hero">
        <p className="policy-eyebrow">Customer care</p>
        <h1>Shipping information</h1>
        <p className="policy-updated">Last updated October 23, 2026</p>
        <p className="policy-intro">
          We carefully prepare each {siteConfig.brandName} order and keep you
          informed from checkout to delivery. Find current processing times,
          delivery options, and tracking guidance below.
        </p>
      </header>

      <div className="policy-layout">
        <aside className="policy-toc">
          <nav aria-label="On this page">
            <p>On this page</p>
            <ol>
              <li><a href="#shipping-options">Shipping options</a></li>
              <li><a href="#shipping-processing">Processing and dispatch</a></li>
              <li><a href="#shipping-tracking">Tracking your order</a></li>
              <li><a href="#shipping-address">Address changes</a></li>
              <li><a href="#shipping-international">International delivery</a></li>
              <li><a href="#shipping-delays">Delays and missing parcels</a></li>
              <li><a href="#shipping-packages">Multiple packages</a></li>
              <li><a href="#shipping-duties">Duties, risk, and returns to sender</a></li>
            </ol>
          </nav>
        </aside>

        <article className="policy-content">
          <section id="shipping-options">
            <span className="policy-section-number">01</span>
            <h2>Shipping options</h2>
            <p>
              Available services and exact delivery charges are confirmed at
              checkout after you enter the destination. Estimated transit begins
              after dispatch and does not include order processing time.
            </p>
            <div className="shipping-table-wrap">
              <table className="shipping-table">
                <caption>Shipping method, rate, and transit estimate</caption>
                <thead>
                  <tr>
                    <th scope="col">Method</th>
                    <th scope="col">Estimated transit</th>
                    <th scope="col">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">Standard</th>
                    <td>{siteConfig.shipping.standardDelivery}</td>
                    <td>{formatCurrency(siteConfig.shipping.standardRate)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="policy-note">
              A fixed tracked shipping charge of {formatCurrency(siteConfig.shipping.standardRate)}
              {" "}is added to every order placed for a supported destination.
            </p>
          </section>

          <section id="shipping-processing">
            <span className="policy-section-number">02</span>
            <h2>Processing and dispatch</h2>
            <p>
              In-stock orders are normally prepared within
              {" "}{siteConfig.shipping.processingTime}, excluding weekends and
              holidays. Releases, promotions, and personalized items may require
              additional time; any different estimate appears on the product page.
            </p>
            <p>
              Orders containing pre-order and in-stock items may be held until
              everything is available. Payment review or incomplete delivery
              information can also postpone dispatch.
            </p>
          </section>

          <section id="shipping-tracking">
            <span className="policy-section-number">03</span>
            <h2>Tracking your order</h2>
            <p>
              We email a dispatch confirmation with a tracking link once the
              carrier receives your parcel. Tracking may take up to 24 hours to
              show movement. Check your spam folder and confirm the email used at
              checkout if the message does not arrive.
            </p>
          </section>

          <section id="shipping-address">
            <span className="policy-section-number">04</span>
            <h2>Address changes</h2>
            <p>
              Review your address carefully before paying. If a correction is
              needed, <Link to="/contact">contact client care</Link> immediately
              with your order number and complete address. We cannot guarantee a
              change after an order enters packing or ships, and carriers may
              charge for rerouting or redelivery.
            </p>
          </section>

          <section id="shipping-international">
            <span className="policy-section-number">05</span>
            <h2>International delivery</h2>
            <p>
              We currently ship to {siteConfig.shipping.destinations}. Available
              destinations and services appear at checkout. Restricted locations
              are excluded from the destination selector, and checkout displays
              the complete merchandise and delivery amount before payment.
            </p>
            <p>
              Regional carrier reviews or address verification can extend delivery
              estimates. We always describe parcel contents and values accurately.
            </p>
          </section>

          <section id="shipping-delays">
            <span className="policy-section-number">06</span>
            <h2>Delays and missing parcels</h2>
            <p>
              Weather, customs, regional disruptions, and peak-season volumes
              can affect carrier estimates. If tracking has not moved for five
              business days, or says delivered but the parcel is missing, check
              nearby entrances and neighbors before contacting us. We will help
              open a carrier inquiry; investigation timelines vary by service.
            </p>
          </section>

          <section id="shipping-packages">
            <span className="policy-section-number">07</span>
            <h2>Multiple packages</h2>
            <p>
              A large order may arrive in more than one parcel for safe packing.
              Each parcel receives its own tracking number. If an item remains
              missing after all parcels show delivered, send the order number and
              package photos to
              {" "}<a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a>.
            </p>
          </section>

          <section id="shipping-duties">
            <span className="policy-section-number">08</span>
            <h2>Duties, risk, and returns to sender</h2>
            <p>
              Unless checkout expressly says duties are included, destination
              duties, import taxes, customs assessments, and local handling fees
              are not part of our delivery charge and may be collected by the
              carrier or authority. We do not mark an order as a gift or declare
              an inaccurate value.
            </p>
            <p>
              Risk passes only when the law applicable to your purchase permits
              it. If a parcel is returned because it was refused, unclaimed, or
              addressed incorrectly, we will contact you about lawful reshipment
              or refund options and any carrier costs that may apply. Mandatory
              remedies for loss or non-delivery are not limited by this policy.
            </p>
          </section>

          <nav className="policy-related" aria-label="Related policies">
            <p>More customer care</p>
            <div>
              <Link to="/returns">Returns &amp; exchanges <span aria-hidden="true">&rarr;</span></Link>
              <Link to="/terms">Terms of service <span aria-hidden="true">&rarr;</span></Link>
              <Link to="/contact">Contact client care <span aria-hidden="true">&rarr;</span></Link>
            </div>
          </nav>
        </article>
      </div>
    </main>
  );
}

export default Delivery;
